import React, { FC, useEffect, useRef, useState, useMemo } from 'react'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { Promotion as PromotionService } from '../../../services/Promotion'
import { Promotion as PromotionType } from '../../../types'
import { Layout } from '../../complexes/Layout'
import { Header } from '../../simples/tablePages/Header'
import { TopPanel } from '../../simples/tablePages/TopPanel'
import { ItemsList } from '../../simples/tablePages/ItemsList'
import {
    PromotionEditor,
    PromotionParamsType,
} from '../../complexes/PromotionEditor'
import { Confirm } from '../../simples/Confirm'
import { useAppDispatch, useAppSelector } from '../../hooks/store'
import {
    loadAutomats,
    loadProducts,
    selectAutomats,
    selectProducts,
} from '../../../store/slices/storage'

import styles from './Promotion.module.scss'

const Promotion: FC = () => {
    const { t } = useTranslation()
    const header = [
        t('427_Promotion_productsList'),
        t('428_Promotion_price'),
        t('429_Promotion_startDate'),
        t('430_Promotion_endDate'),
        t('431_Promotion_automate'),
    ]

    const [promotions, setPromotions] = useState<Array<PromotionType>>([])
    const promotionCurrent = useRef<Array<PromotionType>>(promotions)
    promotionCurrent.current = promotions
    const [loading, setLoading] = useState(false)
    const offset = useRef(0)
    const search = useRef('')
    const has = useRef(true)

    const automats = useAppSelector(selectAutomats)
    const products = useAppSelector(selectProducts)

    const [showPromotionEditor, setShowPromotionEditor] = useState(false)
    const [editedPromotion, setEditedPromotion] =
        useState<PromotionParamsType>()

    const [deletedPromotionId, setDeletedPromotionId] = useState(0)

    const dispatch = useAppDispatch()

    const load = async () => {
        if (!has.current || loading) {
            return
        }

        setLoading(true)
        const result = await PromotionService.getPromotions({
            offset: offset.current,
            search: search.current,
        })
        setLoading(false)

        if (!result.length) {
            has.current = false
            return
        }

        offset.current = offset.current + result.length
        setPromotions([...promotionCurrent.current, ...result])
    }

    const clear = () => {
        has.current = true
        offset.current = 0
        search.current = ''
        setPromotions([])
    }

    useEffect(() => {
        load().then()
        dispatch(loadProducts())
        dispatch(loadAutomats())
    }, [])

    const handleEndReached = async () => {
        await load()
    }

    const handleSearch = async (text: string) => {
        clear()
        search.current = text
        await load()
    }

    const startCreatePromotion = () => {
        setShowPromotionEditor(true)
    }

    const startUpdatePromotion = (promotionId: number) => {
        const promotion = promotions.find((b) => b.id === promotionId)

        if (!promotion) {
            return
        }

        setShowPromotionEditor(true)
        setEditedPromotion({
            ...promotion,
            startAt: moment(promotion.startAt, 'YYYY-MM-DD').format(
                'DD.MM.YYYY'
            ),
            endAt: moment(promotion.endAt, 'YYYY-MM-DD').format('DD.MM.YYYY'),
        })
    }

    const handleSubmit = async (savedPromotion: PromotionType) => {
        let updated = false
        let newPromotions = promotions.map((b) => {
            if (b.id === savedPromotion.id) {
                updated = true
                return savedPromotion
            }

            return b
        })

        if (!updated) {
            newPromotions = [savedPromotion, ...newPromotions]
        }

        setPromotions(newPromotions)

        closePromotionEditor()
    }

    const closePromotionEditor = () => {
        setShowPromotionEditor(false)
        setEditedPromotion(undefined)
    }

    const startDeletePromotion = (promotionId: number) => {
        setDeletedPromotionId(promotionId)
    }

    const cancelPromotionDelete = () => {
        setDeletedPromotionId(0)
    }

    const confirmPromotionDelete = async () => {
        setDeletedPromotionId(0)
        await PromotionService.delete(deletedPromotionId)
        setPromotions(
            promotions.filter(
                (promotion) => promotion.id !== deletedPromotionId
            )
        )
    }

    const rows = useMemo(() => {
        return promotions.map((promotion) => ({
            id: promotion.id,
            values: [
                `${products.find((i) => i.id === promotion.product.id)?.name}`,
                `${promotion.price}`,
                <span className={styles.dateData}>
                    {moment(promotion.startAt).format('DD.MM.YYYY')}
                </span>,
                <span className={styles.dateData}>
                    {moment(promotion.endAt).format('DD.MM.YYYY')}
                </span>,
                `${automats.find((i) => i.id === promotion.automat.id)?.name}`,
            ],
        }))
    }, [promotions, automats, products])

    return (
        <Layout onEndReached={handleEndReached}>
            <Header text={t('432_Promotion_baseCreation')} />
            <TopPanel
                createButtonName={t('433_Promotion_createPromotion')}
                onCreateButtonClick={startCreatePromotion}
                onSearch={handleSearch}
            />
            <ItemsList
                headers={header}
                rows={rows}
                loading={loading}
                onEdit={startUpdatePromotion}
                onDelete={startDeletePromotion}
            />

            {showPromotionEditor && (
                <PromotionEditor
                    promotion={editedPromotion}
                    onSubmit={handleSubmit}
                    onClose={closePromotionEditor}
                />
            )}

            {!!deletedPromotionId && (
                <Confirm
                    text={t('434_Promotion_confirmDelete')}
                    onConfirm={confirmPromotionDelete}
                    onCancel={cancelPromotionDelete}
                />
            )}
        </Layout>
    )
}

export default Promotion
